import { ANALYTICS_CHART_TYPE } from "@resources/Constants";
import { useTheme } from "styled-components";
import {
  CalendarModeRangeType,
  TimeInterval,
  TypeTimeInterval
} from "@custom-types/Types";
import ECharts from "@components/common/chart";
import { HVDropdown, theme as HVTheme } from "@repo/HVComponents";
import { useAnalyticsChartMenu } from "@hooks/useChartSelectMenu";
import {
  chartTooltipFormatter,
  getAxisIntervalLabels,
  xAxisLabelFormatter
} from "@utils/Widget";
import { convertHexToRgb } from "@utils/Global";
import {
  EChartsOption,
  TooltipComponentFormatterCallbackParams
} from "echarts";
import { Theme } from "@themes/Types";
import { useMemo } from "react";
import { isArray } from "lodash";
import { CategorySkeleton } from "src/pages/analytics/skeleton/CategorySkeleton";
import { useLang } from "@hooks/useLang";
import { commonTooltipSettings } from "@components/report/widget/widget-statistics/helper";
import { useAppSelector } from "@configs/Redux";
import * as S from "./PeopleQueueChart.styled";
import { ChartCardProps } from "../Interfaces";

export const RangeLegend = ({ theme }: { theme: Theme }) => {
  const ranges = [
    { label: "0", color: "var(--analytics-chart-widget-bg)" },
    {
      label: "1~5",
      color: `rgba(${convertHexToRgb(
        theme.widget.shoppingCartQueue.systemBlue
      )}, 0.2)`
    },
    {
      label: "6~10",
      color: `rgba(${convertHexToRgb(
        theme.widget.shoppingCartQueue.systemBlue
      )}, 0.4)`
    },
    {
      label: "11~20",
      color: `rgba(${convertHexToRgb(
        theme.widget.shoppingCartQueue.systemBlue
      )}, 0.6)`
    },
    {
      label: "20+",
      color: `rgba(${convertHexToRgb(
        theme.widget.shoppingCartQueue.systemBlue
      )}, 0.8)`
    }
  ];

  return (
    <S.LegendContainer>
      {ranges.map((range) => (
        <S.LegendItem key={range.label}>
          <S.ColorBox color={range.color} />
          <S.Label>{range.label}</S.Label>
        </S.LegendItem>
      ))}
    </S.LegendContainer>
  );
};
export const PeopleQueueChartPresenter = (props: ChartCardProps) => {
  const {
    analyticsData,
    baseTime,
    timeInterval,
    typeInterval,
    setType,
    setIsLoadingChart,
    type,
    isLoading
  } = props;

  const theme = useTheme();
  const { translate } = useLang();
  const { lang } = useAppSelector((state) => state.system);
  const langLocal = lang.toLocaleLowerCase();
  const intervalMenuList = useAnalyticsChartMenu({
    baseTime,
    isForPeopleQueuesAnalytics: true
  });

  const queueNameList = Array.from(analyticsData || []).map(
    (device) => device.locationName
  );

  const convertQueueLevel = (queueLevel?: (number | undefined)[]) => {
    if (!queueLevel) return 0;
    const value = queueLevel[2] as number;

    if (value <= 0) return 0;
    if (value <= 5) return 1;
    if (value <= 10) return 2;
    if (value <= 20) return 3;
    if (value > 20) return 4;

    return 0;
  };

  const renderTooltip = (
    timeInterval: TimeInterval,
    mode: CalendarModeRangeType
  ): EChartsOption["tooltip"] => ({
    formatter: (params: TooltipComponentFormatterCallbackParams) => {
      if (!isArray(params) && isArray(params?.data)) {
        return chartTooltipFormatter(
          timeInterval,
          params?.name,
          [
            {
              label: "Count",
              data: String(params?.data[2])
            }
          ],
          "",
          mode
        );
      }
      return "";
    },
    ...commonTooltipSettings()
  });

  const options = useMemo(() => {
    const graphChart = (analyticsData || []).flatMap(
      (device, index) =>
        device.originalData?.map((deviceData, indexDeviceData) => [
          indexDeviceData,
          index,
          Math.round(deviceData.count),
          Math.round(deviceData.time),
          deviceData.level
        ])
    );
    const { start, end, mode } = baseTime;
    const result = getAxisIntervalLabels(mode, start, end, timeInterval);

    const { labels: xAxisLabels } = result;

    return {
      tooltip: renderTooltip(timeInterval, mode),
      grid: {
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        show: false,
        containLabel: true,
        borderColor: "transparent"
      },
      xAxis: [
        {
          type: "category",
          nameTextStyle: {
            color: theme.widget.shoppingCartQueue.textPrimary,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            verticalAlign: "top",
            align: "right",
            padding: [7, 0, 0, 0]
          },
          axisLabel: {
            color: theme.chart.colorNewAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            formatter: xAxisLabelFormatter(timeInterval, mode, true, langLocal),
            width: 200,
            overflow: "breakAll",
            hideOverlap: true,
            fontWeight: 400,
            verticalAlign: "middle"
          },
          data: xAxisLabels,
          boundaryGap: true,
          axisLine: {
            onZero: false,
            lineStyle: {
              opacity: 0
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: theme.widget.shoppingCartQueue.backgroundColorTooltip
            }
          }
        }
      ],
      yAxis: [
        {
          type: "category",
          data: queueNameList,
          boundaryGap: true,
          axisLabel: {
            color: theme.chart.colorNewAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "left",
            margin: 20,
            padding: [0, 0, 0, -49],
            formatter: (value: string) => {
              if (value.length > 8) {
                return `${value.substring(0, 8)}...`;
              }
              let label = value;
              if (value.length < 9) {
                label = value + " ".repeat(12 - value.length);
              }

              return label;
            },
            fontWeight: 400
          },
          axisLine: {
            onZero: false,
            lineStyle: {
              opacity: 0
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: theme.widget.shoppingCartQueue.backgroundColorTooltip
            }
          },
          splitArea: {
            show: false,
            areaStyle: {
              color: [theme.chart.chartScatteredNoneBg]
            }
          }
        }
      ],
      visualMap: {
        min: 0,
        type: "piecewise",
        show: false,
        calculable: true,
        orient: "horizontal",
        pieces: [
          {
            value: 0,
            color: theme.chart.chartScatteredNoneBg
          },
          {
            value: 1,
            color: `rgba(${convertHexToRgb(
              theme.widget.shoppingCartQueue.systemBlue
            )}, 0.2)`
          },
          {
            value: 2,
            color: `rgba(${convertHexToRgb(
              theme.widget.shoppingCartQueue.systemBlue
            )}, 0.4)`
          },
          {
            value: 3,
            color: `rgba(${convertHexToRgb(
              theme.widget.shoppingCartQueue.systemBlue
            )}, 0.6)`
          },
          {
            value: 4,
            color: `rgba(${convertHexToRgb(
              theme.widget.shoppingCartQueue.systemBlue
            )}, 0.8)`
          }
        ]
      },
      series: [
        {
          type: "heatmap",
          data: graphChart.map(
            (item) =>
              item && [item[0], item[1], item[2], convertQueueLevel(item)]
          ),
          label: {
            show: false,
            color: theme.widget.shoppingCartQueue.textPrimary
          },
          itemStyle: {
            borderWidth: 2,
            borderRadius: 3,
            borderColor: theme.widget.shoppingCartQueue.backgroundColorTooltip
          },
          emphasis: {
            itemStyle: {
              borderWidth: 2,
              borderRadius: 3,
              borderColor: theme.widget.shoppingCartQueue.backgroundColorTooltip
            }
          }
        }
      ]
    };
  }, [
    JSON.stringify(theme),
    baseTime.start,
    baseTime.end,
    baseTime.mode,
    timeInterval,
    analyticsData,
    lang
  ]);

  const getQueueTitle = () => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
        return "people_queue_status";
      case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
        return "vehicle_queue_status";
      case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
        return "shopping_carts_queue_status";
      default:
        return "no_data";
    }
  };
  const updateSetTimeInterval = (value: TypeTimeInterval) => {
    setType?.(value);
    props.updatedAnalytics?.([], value, null, props.isSingleLocation);
  };

  if (
    type !== ANALYTICS_CHART_TYPE.PEOPLE_QUEUES &&
    type !== ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES &&
    type !== ANALYTICS_CHART_TYPE.VEHICLE_QUEUES
  )
    return null;

  return (
    <S.ChartWrapper>
      <S.ChartHeader>
        <S.ChartTitle>{translate(getQueueTitle())}</S.ChartTitle>
      </S.ChartHeader>

      <S.LegendWrapper>
        <RangeLegend theme={theme} />
        {!props.analyticsFromReport && (
          <HVDropdown
            type="medium"
            w={`calc(16px + ${typeInterval.toLowerCase().length}ch)`}
            miw={120}
            maw={92}
            options={intervalMenuList}
            selectedItem={typeInterval || null}
            styles={{
              input: {
                borderRadius: 4,
                height: 24,
                borderColor: "var(--border-default)",
                background: "transparent",
                minHeight: 24,
                fontSize: 12,
                fontWeight: 400,
                color: "var(--hv-text-normal-secondary)",
                paddingRight: 14
              },
              section: {
                width: 9,
                marginRight: 5
              },
              dropdown: {
                minWidth: 100
              }
            }}
            getSelectedValue={(value) => {
              value && updateSetTimeInterval(value as TypeTimeInterval);
              setIsLoadingChart?.(false);
            }}
            selectWithArrow={false}
          />
        )}
      </S.LegendWrapper>

      {isLoading ? (
        <CategorySkeleton singleBlockLoader minHeight="320px" />
      ) : (
        <ECharts
          option={options as EChartsOption}
          clearAfterUpdateChart
          style={{
            pointerEvents: "all",
            height: "320px"
          }}
        />
      )}
    </S.ChartWrapper>
  );
};
